import { 
    Box,
    CircularProgress,
    Paper, 
    Stack, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TablePagination, 
    TableRow, 
} from "@mui/material"
import { useStore } from "../../../app/stores/store";
import ObjectTypeFilter from "./Menu/ObjectTypeFilter";
import { observer } from "mobx-react-lite";
import ProgressFilter from "./Menu/ProgressFilter";
import SearchFilter from "./Menu/SearchFilter";
import { useTable } from "./hook/useTable";
import { useFetch } from "./hook/useFetch";
import HomeTableEquipmentButtons from "./HomeTableEquipmentTableButtons";
import HomeLegend from "../common/HomeLegend";
import { EquipmentColors } from "../../../app/config/enum";

type Align = 'right' | 'center' | 'left';

interface Column {
    id: keyof HomeEquipmentData;
    label: string;
    minWidth?: number;
    align: Align;
    format?: (value: number) => string;
}

const columns: readonly Column[] = [
    { 
        id: 'tagNumber',
        label: 'Tag\u00a0Number',
        minWidth: 200,
        align: 'left'
    },
    {
        id: 'systemName',
        label: 'System',
        minWidth: 100,
        align: 'left',
    },
    {
        id: 'objectType',
        label: 'Object\u00a0Type',
        minWidth: 50,
        align: 'left',
    },
    {
        id: 'reliability',
        label: 'Reliability',
        minWidth: 150,
        align: 'center',
    },
    {
        id: 'availability',
        label: 'Availability',
        minWidth: 150,
        align: 'center',
    },
    {
        id: 'maintainability',
        label: 'Maintainability',
        minWidth: 100,
        align: 'center'
    },
];

export interface HomeEquipmentData {
    tagNumber: string;
    systemName: string;
    objectType: string;
    reliability: React.ReactNode;
    availability: React.ReactNode;
    maintainability: React.ReactNode;
}

export function createHomeEquipmentData(
    tagNumber: string,
    systemName: string,
    objectType: string,
    reliability: React.ReactNode,
    availability: React.ReactNode,
    maintainability: React.ReactNode,
): HomeEquipmentData {
  return { tagNumber, systemName, objectType, reliability, availability, maintainability };
}

const HomeTableEquipmentTable = () => {
    useFetch()

    const { equipmentRam, loadingEquipmentRam } = useStore().homeTableEquipmentStore
    
    const { 
        rows, 
        rowsPerPage, 
        handleChangePage, 
        handleChangeRowsPerPage, 
        page,
        handleFilter, 
        filter, 
        handleClear, 
        handleClearAll, 
        equipmentType,
        isFilterApplied,
    } = useTable(equipmentRam)
    
    if (loadingEquipmentRam) {
        return (
            <Stack justifyContent="center" direction="row">
                <CircularProgress />
            </Stack>
        )
    }
    
    return (
        <>  
            <HomeTableEquipmentButtons isFilterApplied={isFilterApplied} clearAllFilter={handleClearAll} />
            <Box mb={3} />
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: '500px' }}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                {columns.map((column, x) => (
                                    <TableCell
                                        key={x}
                                        align="center"
                                        style={{ minWidth: column.minWidth }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            {column.id === 'objectType' && 
                                                <ObjectTypeFilter 
                                                    list={equipmentType}
                                                    filtered={filter.equipmentType}
                                                    handleChange={handleFilter}
                                                    handleClear={handleClear}
                                                    session="equipmentType"
                                                />
                                            }
                                            {(column.id === 'tagNumber' || 
                                                column.id === 'systemName') && (
                                                    <SearchFilter 
                                                        session={column.id} 
                                                        handleChange={handleFilter}
                                                        handleClear={handleClear}
                                                        value={filter[column.id] as any}
                                                    />
                                                )
                                            }
                                            {(column.id === 'reliability' || 
                                                column.id === 'availability' || 
                                                column.id === 'maintainability') && (
                                                    <ProgressFilter 
                                                        session={
                                                            column.id !== 'reliability' 
                                                                ? column.id 
                                                                : 'probabilityOfSuccess'
                                                        } 
                                                        filtered={
                                                            column.id !== 'reliability' 
                                                                ? filter[column.id]
                                                                : filter['probabilityOfSuccess']
                                                        }
                                                        handleChange={handleFilter}
                                                        handleClear={handleClear}
                                                    />
                                                )
                                            }
                                            {column.label}
                                        </Stack>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, x) => {
                                    return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={x}>
                                        {columns.map((column, idx) => {
                                            const value = row[column.id];
                                            return (
                                                <TableCell key={idx} align={column.align}>
                                                {column.format && typeof value === 'number'
                                                    ? column.format(value)
                                                    : value}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                <Stack pb={2} direction="row" justifyContent="center" spacing={2}>
                    <HomeLegend color={EquipmentColors.Red} label="Less than 50%"/>
                    <HomeLegend color={EquipmentColors.Orange} label="Between 50% and 70%"/>
                    <HomeLegend color={EquipmentColors.Yellow} label="Between 70% and 90%"/>
                    <HomeLegend color={EquipmentColors.Green} label="More than 90%"/>
                </Stack>
            </Paper>
        </>
    )
}

export default observer(HomeTableEquipmentTable)